import apiService, { ApiError } from './apiService';

/**
 * Reversal Service
 * Handles all reversal transaction-related API operations
 */
class ReversalService {
  /**
   * Format date to API required format (YYYY-MM-DD HH:mm:ss)
   * @param {string|Date} date - Date to format
   * @param {boolean} isEndDate - Whether this is end date (adds 23:59:59)
   * @returns {string} Formatted date string
   */
  formatDateForAPI(date, isEndDate = false) {
    if (!date) return null;

    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return null;

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    const time = isEndDate ? '23:59:59' : '00:00:00';

    return `${year}-${month}-${day} ${time}`;
  }

  /**
   * Get list of snap and earn transactions with pagination, search, and filter
   * @param {Object} params - Filter and pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Items per page (default: 20)
   * @param {string} params.keyword - Search by company name or reference (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {string} params.channel - Transaction channel: A (Approved), P (Pending), R (Rejected) (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Transaction list response
   */
  async getTransactionList(params = {}, options = {}) {
    const { page = 1, size = 20, keyword, dateFrom, dateTo, channel } = params;

    try {
      // Build request payload
      const requestData = {
        page,
        size,
      };

      // Add keyword if provided
      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      // Add channel if provided
      if (channel) {
        requestData.channel = channel;
      }

      // Add dateFilter if dates are provided
      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiOneRequest(
        '/api/v1/admin/reversal/list',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      // Validate response structure
      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from reversal transaction list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch reversal transaction list:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve transaction list',
        0,
        'SNAP_EARN_LIST_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Transform API transaction data to frontend format
   * @param {Object} apiTransaction - Raw transaction data from API
   * @returns {Object} Transformed transaction data
   */
  transformTransactionData(apiTransaction) {
    if (!apiTransaction) return null;
    // Map channel code to display label
    const channelMap = {
      'pay-go': '1 Tap',
      'snap-earn': '1 Snap',
      'manual': 'Manual',
    };

    const statusMap = {
      'S': 'Success',
      'C': 'Cancelled',
    };


    // Map files array to receiptImages (extract signed URLs)
    const receiptImages = (apiTransaction.files || [])
      .filter(file => file.fileType && file.fileType.startsWith('image/'))
      .map(file => file.fileName);
    return {
      id: apiTransaction.id,
      companyName: apiTransaction.companyName || 'N/A',
      sector: apiTransaction.sector || 'N/A',
      outlet: apiTransaction.outlet || 'N/A',
      // Current API documentation shows channel as 'A', 'P', 'R'
      channel:
        channelMap[apiTransaction.channel] || apiTransaction.channel || 'N/A',
      status:
        statusMap[apiTransaction.status.trim()] || apiTransaction.status || 'N/A',
      transactionAmount: apiTransaction.spent || 0,
      transactionDateTime: apiTransaction.transactionDate, // ISO string
      transactionDate: apiTransaction.transactionDate
        ? apiTransaction.transactionDate.split('T')[0]
        : '',
      transactionTime: apiTransaction.transactionDate
        ? apiTransaction.transactionDate.split('T')[1]?.substring(0, 5)
        : '',
      // Receipt images from files array
      receiptImages: receiptImages,
      // Raw files data for other file types if needed
      files: apiTransaction.files || [],
      histories: apiTransaction.histories || [],
      // These are missing from the current API documentation but used in current UI dummy data
      shopperName: apiTransaction.name || 'N/A',
      shopperPhone: apiTransaction.phone || 'N/A',
      cancelledReasonId: apiTransaction.cancelledReasonId,
      cancelledReasonDescription: apiTransaction.cancelledReasonDescription,
    };
  }

  /**
   * Get formatted transaction list with transformed data
   * @param {Object} params - Filter and pagination parameters
   * @returns {Promise<Object>} Formatted transaction list response
   */
  async getFormattedTransactionList(params = {}, options = {}) {
    try {
      const response = await this.getTransactionList(params, options);

      // Transform the data array
      const transformedData = {
        ...response.data,
        data: (response.data.data || []).map(tx =>
          this.transformTransactionData(tx)
        ),
      };

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error('Failed to get formatted transaction list:', error);
      throw error;
    }
  }

  /**
   * Get list of cancelled reasons for Snap & Earn
   * This endpoint does not require authentication
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Rejected reasons list response
   */
  async getCancelReasons(options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/admin/reversal/reason',
        {
          method: 'GET',
          includeAuth: true, // No authentication required
          ...options,
        }
      );

      // Validate response structure
      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from cancel reasons API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch cancel reasons:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve cancel reasons',
        0,
        'CANCEL_REASONS_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get formatted cancel reasons list
   * @param {Object} options - Additional request options
   * @returns {Promise<Array>} Array of cancelled reason objects with id and description
   */
  async getFormattedCancelReasons(options = {}) {
    try {
      const response = await this.getCancelReasons(options);

      // Return the data array directly
      return response.data || [];
    } catch (error) {
      console.error('Failed to get formatted cancelled reasons:', error);
      throw error;
    }
  }
  /**
   * Process transaction approval (Approve/Reject)
   * @param {Object} data - Approval data
   * @param {string} data.snapEarnId - Transaction ID
   * @param {string} data.channel - Status: 'A' (Approve) or 'R' (Reject)
   * @param {number} data.amount - Transaction amount
   * @param {string} [data.cancelledReasonId] - Reason ID (required if channel is 'R')
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Approval response
   */
  async processCancellation(data, options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/admin/reversal/cancel/${data.id}`,
        {
          method: 'POST',
          data: data,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from cancellation API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to process cancellation:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError('Failed to process cancellation', 0, 'CANCELLATION_ERROR', {
        originalError: error.message,
      });
    }
  }
}

// Create singleton instance
const snapEarnService = new ReversalService();

export default snapEarnService;
export { ReversalService };
